import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { resolveUrl } from "@/lib/api";

// photos: string[]; videoUrl optional (shown as first slide)
export const Gallery = ({ photos = [], videoUrl = "", open, startIndex = 0, onClose }) => {
  const slides = [...(videoUrl ? [{ type: "video", src: videoUrl }] : []), ...photos.map((p) => ({ type: "image", src: p }))];
  const [idx, setIdx] = useState(startIndex);

  useEffect(() => { setIdx(startIndex); }, [startIndex, open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % slides.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + slides.length) % slides.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, slides.length]); // eslint-disable-line

  if (!open || !slides.length) return null;
  const go = (d) => setIdx((i) => (i + d + slides.length) % slides.length);
  const cur = slides[idx];

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col" data-testid="gallery-lightbox">
      <div className="flex items-center justify-between p-4 text-white">
        <span className="text-sm font-semibold">{idx + 1} / {slides.length}</span>
        <button onClick={onClose} data-testid="gallery-close" className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/15"><X size={22} /></button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-6 relative">
        {slides.length > 1 && (
          <button onClick={() => go(-1)} data-testid="gallery-prev" className="absolute left-4 h-11 w-11 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25"><ChevronLeft size={24} /></button>
        )}
        {cur.type === "image" ? (
          <img src={resolveUrl(cur.src)} alt="" className="max-h-[80vh] max-w-full object-contain rounded-lg" />
        ) : (
          <video key={cur.src} src={resolveUrl(cur.src)} controls autoPlay playsInline data-testid="gallery-video" className="max-h-[80vh] max-w-full rounded-lg" />
        )}
        {slides.length > 1 && (
          <button onClick={() => go(1)} data-testid="gallery-next" className="absolute right-4 h-11 w-11 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25"><ChevronRight size={24} /></button>
        )}
      </div>
      {slides.length > 1 && (
        <div className="flex gap-2 overflow-x-auto p-4 justify-center">
          {slides.map((s, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`relative h-14 w-20 shrink-0 rounded-lg overflow-hidden ring-2 ${i === idx ? "ring-white" : "ring-transparent opacity-60"}`}>
              {s.type === "image" ? <img src={resolveUrl(s.src)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-slate-700 grid place-items-center text-white"><Play size={16} /></div>}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};
